import React, { useState, useEffect, Fragment } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SlugComponentGuard } from './guards/slug';
import {
  HomePage,
  ModulesPage,
  ProfilePage,
  VerificationPage,
  LoginPage,
  GroupsPage,
  DashboardPokjaPage,
  DashboardSurveyorInternalPage,
  DashboardSurveyorExternalPage,
  UsersPage,
  PokjaPage,
  MapingGroupsPage,
  AccreditationPage,
  ChapterAccreditationPage,
  DocumentSurveiPage,
  MasterStandardsPage,
  MasterChaptersPage,
  MasterElementsPage,
  MasterInstrumentsPage,
  ForumPage,
  PpsPage,
  PpsItemPage,
  PpsItemDokumenPage,
  ManagerFilesPage,
  RiskActivityTargetsPage,
  RiskRegisterPage,
  RiskHeatmapPage,
  RiskDivisionsPage,
} from '../../pages';
import { NavbarComp, WrapperComp } from '../../components';
import { logo } from '../../assets';
import axios from '../../library/axios';
import { useDocumentTitle } from '../../helpers/useDocumentTitle';
import {
  clearHospitalToken,
  getHospitalLoginPath,
  getHospitalToken,
  isHospitalLoginRoute,
} from '../../helpers/hospitalAuth';

const baseRoutes = [
  { path: '', element: <HomePage /> },
  { path: 'accreditation', element: <AccreditationPage /> },
  { path: 'profile', element: <ProfilePage /> },
  { path: 'modules', element: <ModulesPage /> },
  { path: 'groups', element: <GroupsPage /> },
  { path: 'kelompok-kerja', element: <PokjaPage /> },
  { path: 'dashboard-pokja', element: <DashboardPokjaPage /> },
  { path: 'dashboard-internal', element: <DashboardSurveyorInternalPage /> },
  { path: 'dashboard-surveyor-internal', element: <DashboardSurveyorInternalPage /> },
  { path: 'dashboard-surveyor-external', element: <DashboardSurveyorExternalPage /> },
  { path: 'users', element: <UsersPage /> },
  { path: 'maping-groups', element: <MapingGroupsPage /> },
  { path: 'chapter/:id/:idMasterAkreditasi', element: <ChapterAccreditationPage /> },
  { path: 'documents', element: <DocumentSurveiPage /> },
  { path: 'standards-accreditation', element: <MasterStandardsPage /> },
  { path: 'chapters-accreditation', element: <MasterChaptersPage /> },
  { path: 'elements-accreditation', element: <MasterElementsPage /> },
  { path: 'variables-accreditation', element: <MasterInstrumentsPage /> },
  { path: 'forum/:id/:idMasterAkreditasi', element: <ForumPage /> },
  { path: 'pps/:id', element: <PpsPage /> },
  { path: 'pps-item/:id', element: <PpsItemPage /> },
  { path: 'pps-dokumen-item/:id/:idRekomendasi', element: <PpsItemDokumenPage /> },
  { path: 'manager-file', element: <ManagerFilesPage /> },
  { path: 'risk-activity-targets', element: <RiskActivityTargetsPage /> },
  { path: 'risk-register/:id', element: <RiskRegisterPage /> },
  { path: 'risk-heatmap', element: <RiskHeatmapPage /> },
  { path: 'risk-divisions', element: <RiskDivisionsPage /> },
];

const DocumentTitleUpdater = () => {
  useDocumentTitle();
  return null;
};

const HospitalRoutes = () => {
  const [isValidate, setisValidate] = useState(null);
  const [sessionState, setSessionState] = useState('idle');
  const isLoginRoute = isHospitalLoginRoute();
  const hospitalToken = getHospitalToken();

  const fetchData = async () => {
    try {
      const { data } = await axios({
        url: '/profile/list',
        method: 'GET',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      });
      setisValidate(Array.isArray(data) && data.length > 0);
    } catch (err) {
      console.log(err);
      setisValidate(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isLoginRoute) {
      setSessionState('idle');
      return undefined;
    }

    if (!hospitalToken) {
      setSessionState('invalid');
      return undefined;
    }

    let cancelled = false;
    setSessionState('checking');

    axios({
      url: '/users/detailUser',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${hospitalToken}`,
      },
    })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.login === false) {
          clearHospitalToken();
          setSessionState('invalid');
          return;
        }
        setSessionState('valid');
      })
      .catch(() => {
        if (cancelled) return;
        clearHospitalToken();
        setSessionState('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [hospitalToken, isLoginRoute]);

  useEffect(() => {
    const initMetronic = () => {
      window.KTMenu?.createInstances();
      window.KTDrawer?.createInstances();
      window.KTToggle?.createInstances();
      window.KTScroll?.createInstances();
    };
    const timer = setTimeout(initMetronic, 200);
    return () => clearTimeout(timer);
  }, []);

  if (isLoginRoute) {
    return (
      <SlugComponentGuard>
        <LoginPage />
      </SlugComponentGuard>
    );
  }

  if (isValidate === null || (hospitalToken && sessionState === 'checking')) {
    return null;
  }

  if (isValidate === false && !hospitalToken) {
    return (
      <SlugComponentGuard>
        <VerificationPage />
      </SlugComponentGuard>
    );
  }

  if (!hospitalToken || sessionState === 'invalid') {
    return <Navigate to={getHospitalLoginPath()} replace />;
  }

  if (sessionState !== 'valid') {
    return null;
  }

  return (
    <>
      <SlugComponentGuard>
        <DocumentTitleUpdater />
        <div className="d-flex flex-column flex-root">
          <div className="page d-flex flex-row flex-column-fluid">
            <NavbarComp />
            <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
              <div id="kt_header" style={{}} className="header align-items-stretch">
                <div className="container-fluid d-flex align-items-stretch justify-content-between">
                  <div className="d-flex align-items-center d-lg-none ms-n3 me-1" title="Show aside menu">
                    <div
                      className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
                      id="kt_aside_mobile_toggle"
                    >
                      <span className="svg-icon svg-icon-2x mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none">
                          <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="black" />
                          <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="black" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
                    <a href="#" className="d-lg-none">
                      <img alt="Logo" src={logo} className="h-30px" />
                    </a>
                  </div>
                  <WrapperComp />
                </div>
              </div>
              <div className="post d-flex flex-column-fluid">
                <div id="kt_content_container" className="container-xxl">
                  <Routes>
                    {baseRoutes.map(({ path, element }) => (
                      <Fragment key={path || 'home'}>
                        <Route path={path} element={element} />
                      </Fragment>
                    ))}
                  </Routes>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SlugComponentGuard>
    </>
  );
};

export default HospitalRoutes;
